import { Component, inject, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, Validators } from '@angular/forms';
import { noop } from 'rxjs';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: ''
})
export class CustomControl<T> implements ControlValueAccessor, OnInit {
    private readonly controlContainer = inject(ControlContainer, { optional: true });

    public control: FormControl<T | null> = new FormControl<T | null>(null);

    public readonly formControl = input<FormControl<T | null>>();
    public readonly formControlName = input('');

    public onChange: (value: T | null) => void = noop;
    public onTouch: () => void = noop;

    ngOnInit(): void {
        this.control = this.createControl();
    }

    writeValue(value: T): void {
        if (this.control.value === value) {
            return;
        }
        this.control.setValue(value, { emitEvent: false });
    }

    registerOnChange(onChange: (value: T | null) => void): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouch: () => void): void {
        this.onTouch = onTouch;
    }

    setDisabledState(isDisabled: boolean): void {
        // `control` may be the same instance the CVA is bound to, which would re-enter via `_onDisabledChange`.
        if (this.control.disabled === isDisabled) {
            return;
        }

        if (isDisabled) {
            this.control.disable({ emitEvent: false });
            return;
        }

        this.control.enable({ emitEvent: false });
    }

    isRequired(): boolean {
        return this.control?.hasValidator?.(Validators.required) ?? false;
    }

    createErrorMessage(errorMessages?: Record<string, string>): string {
        const errors = this.control.errors;
        if (!errors) return '';
        const firstErrorKey = Object.keys(errors)?.[0];
        return firstErrorKey ? errorMessages?.[firstErrorKey] || '' : '';
    }

    private createControl(): FormControl<T | null> {
        if (this.formControl?.()) {
            return this.formControl() as FormControl<T | null>;
        }

        const name = this.formControlName?.();
        if (name && this.controlContainer?.control) {
            const ctrl = this.controlContainer.control.get(name);
            if (ctrl) return ctrl as FormControl<T | null>;
        }

        return new FormControl<T | null>(null);
    }
}
